import { Injectable }       from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import userflow             from 'userflow.js';

import {
  AI_SUPPORT_ALLOWED_LINK_PREFIXES,
  AI_SUPPORT_MAX_QUESTION,
  AI_SUPPORT_OPEN_FLAG,
  AI_SUPPORT_OPEN_VALUE,
  AI_SUPPORT_TIMEOUT_MS
}                                         from './ai-support.constants';
import { AiSupportAskResponseModel }      from './typings/ai-support.ask-response';
import { AiSupportEscalateResponseModel } from './typings/ai-support.escalate-response';
import { AiSupportMessageModel }          from './typings/ai-support.message';
import { AiSupportReplyModel }            from './typings/ai-support.reply';
import { AiSupportScreenModel }           from './typings/ai-support.screen';
import { AiSupportSegmentModel }          from './typings/ai-support.segment';
import { ApiService }                     from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class AiSupportService {
  private _messageId = 0;

  constructor (
    private apiService      : ApiService,
    private translateService: TranslateService
  ) {}

  private _isAllowedUrl ( url : string ) : boolean {
    return AI_SUPPORT_ALLOWED_LINK_PREFIXES.some(prefix => url.startsWith(prefix));
  }

  private _nextId () : number {
    return ++this._messageId;
  }

  private _removeFlag ( query : string ) : string {
    const params = new URLSearchParams(query);

    params.delete(AI_SUPPORT_OPEN_FLAG);

    const rest = params.toString();

    return rest ? `?${ rest }` : '';
  }

  // The panel must not hang on a slow answer, so the request is cut off after a minute.
  private _timeBox<T> ( request : Promise<T> ) : Promise<T> {
    let timer : ReturnType<typeof setTimeout>;

    const timeout = new Promise<T>(( resolve, reject ) => {
      timer = setTimeout(() => reject({
        message: this.translateService.instant('AI_SUPPORT_SPACE.PANEL.TIMED_OUT'),
        type   : 'G'
      }), AI_SUPPORT_TIMEOUT_MS);
    });

    return Promise.race([ request, timeout ])
    .finally(() => clearTimeout(timer));
  }

  private _toReply ( response : AiSupportAskResponseModel ) : AiSupportReplyModel {
    const isContinuing = !!response.continuing;

    return {
      conversationId    : response.conversation_id,
      isContinuing,
      isConversationFull: response.exchanges_left === 0,
      isLimited         : !!response.limit_reached || !!response.suspended || response.exchanges_left === 0,
      message           : isContinuing ? null : {
        id                 : this._nextId(),
        isEscalationOffered: !!response.escalation_offered,
        isUser             : false,
        segments           : this.parseAnswer(response.answer || '')
      }
    };
  }

  ask ( conversationId : string | null, question : string, screen : AiSupportScreenModel ) : Promise<AiSupportReplyModel> {
    return this._timeBox(this.apiService.post('/ai_support/ask', {
      conversation_id: conversationId,
      question       : this.cleanQuestion(question),
      screen
    }))
    .then(( response : AiSupportAskResponseModel ) => this._toReply(response));
  }

  cleanQuestion ( question : string ) : string {
    // Control characters other than tab and newline are stripped, as the server does.
    // eslint-disable-next-line no-control-regex
    return ( question || '' ).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
      .trim()
      .slice(0, AI_SUPPORT_MAX_QUESTION);
  }

  close ( conversationId : string ) : Promise<void> {
    return this.apiService.post('/ai_support/close', { conversation_id: conversationId });
  }

  closeHelpCentre () : void {
    userflow.closeResourceCenter();
  }

  // Carries on an answer the server paused before its request time ran out.
  continue ( conversationId : string ) : Promise<AiSupportReplyModel> {
    return this._timeBox(this.apiService.post('/ai_support/continue', { conversation_id: conversationId }))
    .then(( response : AiSupportAskResponseModel ) => this._toReply(response));
  }

  createUserMessage ( question : string ) : AiSupportMessageModel {
    return {
      id                 : this._nextId(),
      isEscalationOffered: false,
      isUser             : true,
      segments           : [ { text: question } ]
    };
  }

  escalate ( conversationId : string ) : Promise<string> {
    return this.apiService.post('/ai_support/escalate', { conversation_id: conversationId })
    .then(( response : AiSupportEscalateResponseModel ) => response.reference);
  }

  hasOpenFlag () : boolean {
    return this.isOpenLink(window.location.href);
  }

  // A Workshop Assist link carrying the flag opens the panel over the current record instead of navigating.
  interceptOpenLinks ( open : () => void ) : void {
    userflow.setCustomNavigate(( url : string ) => {
      if (this.isOpenLink(url)) {
        open();
      }
      else {
        window.location.href = url;
      }
    });
  }

  isOpenLink ( url : string ) : boolean {
    const [ beforeHash, hash ] = ( url || '' ).split('#');
    const queries              = [ beforeHash.split('?')[1], ( hash || '' ).split('?')[1] ];

    return queries.some(query => new URLSearchParams(query || '').get(AI_SUPPORT_OPEN_FLAG) === AI_SUPPORT_OPEN_VALUE);
  }

  releaseOpenLinks () : void {
    userflow.setCustomNavigate(null);
  }

  // Links are only kept for app routes and our knowledge base; anything else shows as its label.
  // App routes open in place; knowledge base articles open a new tab so the app stays open.
  parseAnswer ( answer : string ) : Array<AiSupportSegmentModel> {
    const pattern  = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
    const segments : Array<AiSupportSegmentModel> = [];
    const text     = answer || '';

    let last  = 0;
    let match : RegExpExecArray | null;

    while (( match = pattern.exec(text) ) !== null) {
      if ( match.index > last ) {
        segments.push({ text: text.slice(last, match.index) });
      }

      if (this._isAllowedUrl(match[2])) {
        segments.push({ isExternal: !match[2].startsWith('#/'), label: match[1], url: match[2] });
      }
      else {
        segments.push({ text: match[1] });
      }

      last = pattern.lastIndex;
    }

    if ( last < text.length ) {
      segments.push({ text: text.slice(last) });
    }

    return segments;
  }

  removeOpenFlag () : void {
    const { hash, pathname, search } = window.location;
    const [ hashPath, hashQuery ]    = hash.split('?');

    const newHash = hashQuery === undefined ? hash : `${ hashPath }${ this._removeFlag(hashQuery) }`;
    const url     = `${ pathname }${ this._removeFlag(search) }${ newHash }`;

    window.history.replaceState(window.history.state, '', url);
  }

  screenFor ( state : string, params : { [ key : string ] : any } ) : AiSupportScreenModel {
    const screenParams : { [ key : string ] : number | string } = {};

    Object.keys(params || {})
      .filter(key => key === 'id' || key.endsWith('_id'))
      .filter(key => typeof params[key] === 'string' || typeof params[key] === 'number')
      .forEach(key => screenParams[key] = params[key]);

    return {
      params: screenParams,
      state : state || ''
    };
  }
}
